import { NgFor, NgIf } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material';
import { isNil } from 'lodash';
import { AccountStructure } from '@app/models/fund-management';
import { AfterReportHandlerService } from '@app/modules/fund-management/reports/services/after-report-handler.service';
import { AccountStructureService } from '@app/shared/services';
import { AccountElementModel } from './account-element.models';

@Component({
	selector: 'account-range-table',
	template: `<div cg-busy="{promise:loadingElementRangeGrid, message: 'Loading....' }">
		<div class="pull-right">
			<button (click)="clearAll()" class="linq-button--primary clear-button" type="button" color="primary" mat-flat-button>Clear</button>
		</div>
		<form [formGroup]="rangeForm">
			<table class="table table-hover" style="border: 1px solid; border-color: darkgray;" formArrayName="rows">
				<thead>
					<tr>
						<th style="width: 180px;">
							<mat-label>Element</mat-label>
						</th>
						<th>
							<mat-label> From </mat-label>
						</th>
						<th>
							<mat-label>To</mat-label>
						</th>
					</tr>
				</thead>

				<tbody>
					<tr *ngFor="let row of rangeForm.get('rows')?.controls; let i = index" [formGroupName]="i">
						<td>{{ this.props.required ? RangeTableModel[i].element + ' *' : RangeTableModel[i].element }}</td>
						<td>
							<input
								type="text"
								formControlName="fromValue"
								[allowDecimals]="false"
								[maxlength]="RangeTableModel[i].maxLength"
								(ngModelChange)="onChange(RangeTableModel[i], true)"
								(paste)="onPaste($event, RangeTableModel[i], true)"
							/>
							<p class="error-label" *ngIf="row.hasError('fromLessThanTo')">'From' value must be less or equal than 'To' value.</p>
							<p class="error-label" *ngIf="row.hasError('fromRequired') || row.hasError('toValueExists')">
								Element must have a 'From' value.
							</p>
							<p class="error-label" *ngIf="row.hasError('bothMustBeDigitOrX')">
								'From' and 'To' values must both be digits or both be X.
							</p>
						</td>
						<td>
							<input
								type="text"
								formControlName="toValue"
								[allowDecimals]="false"
								[maxlength]="RangeTableModel[i].maxLength"
								(ngModelChange)="onChange(RangeTableModel[i], false)"
								(paste)="onPaste($event, RangeTableModel[i], false)"
							/>
							<p class="error-label" *ngIf="row.hasError('fromLessThanTo')">'From' value must be less or equal than 'To' value.</p>
							<p class="error-label" *ngIf="row.hasError('toRequired')">Element must have a 'To' value.</p>
							<p class="error-label" *ngIf="row.hasError('bothMustBeDigitOrX')">
								'From' and 'To' values must both be digits or both be X.
							</p>
						</td>
					</tr>
				</tbody>
			</table>
		</form>
	</div> `,
	styles: [
		`
			.clear-button {
				margin-right: var(--linq-spacing-1);
				margin-bottom: var(--linq-spacing-1);
			}
			input {
				min-width: 70%;
			}
			.error-label {
				color: red;
				margin: 0;
			}
		`,
	],
	standalone: true,
	imports: [MatButtonModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, NgFor, NgIf],
})
export class AccountRangeTableComponent extends FieldType<FormlyFieldConfig> implements OnInit {
	static readonly elementValidator = /^(\d+|[xX])$/;
	rangeForm: UntypedFormGroup;
	siteOnly = false;
	initValues: boolean;
	RangeTableModel: Array<AccountElementModel> = [];

	constructor(
		private readonly formBuilder: UntypedFormBuilder,
		private readonly accountStructureService: AccountStructureService,
		private readonly afterReportService: AfterReportHandlerService
	) {
		super();
		this.rangeForm = this.formBuilder.group({
			rows: this.formBuilder.array([]),
		});
	}

	ngOnInit(): void {
		this.loadOptions();
		this.afterReportService.handler.subscribe((data) => {
			this.RangeTableModel = data;
		});
		this.rangeForm.valueChanges.subscribe((data) => {
			this.value = data.rows;
		});
	}

	loadOptions() {
		this.accountStructureService.getStructure().subscribe((response) => {
			if (!this.RangeTableModel) {
				this.RangeTableModel = [];
			}
			const accountStructure = response.accountStructure as AccountStructure;

			if (accountStructure.UnifiedElementMap) {
				const elements = accountStructure.UnifiedElementMap.split('.');
				const masks = accountStructure.UnifiedAccountMask.split('.');

				if (this.props.siteOnly) {
					const siteElement = elements[accountStructure.UnifiedElementSiteIndex];
					const siteMask = masks[accountStructure.UnifiedElementSiteIndex];

					let startIdx = 1;
					for (let mIdx = 0; mIdx < accountStructure.UnifiedElementSiteIndex; mIdx++) {
						startIdx += masks[mIdx].length;
					}

					const elementModel = this.createElementModel(siteElement, siteMask, startIdx);
					this.RangeTableModel.push(elementModel);
				} else {
					// build list for elements
					const RangeTableModel = elements.map((element, i) => {
						let startIdx = 1;
						for (let mIdx = 0; mIdx < i; mIdx++) {
							startIdx += masks[mIdx].length;
						}
						return this.createElementModel(element, masks[i], startIdx);
					});

					this.RangeTableModel.push(...RangeTableModel);
				}
				this.value = this.RangeTableModel;
				const rowsFormArray = this.rangeForm.get('rows') as UntypedFormArray;
				this.RangeTableModel.forEach((element) => {
					rowsFormArray.push(this.createRowFormGroup(element));
				});
				if (!this.form.get('accountRangeTable')) {
					(this.form as UntypedFormGroup).addControl('accountRangeTable', this.rangeForm);
				} else {
					(this.form as UntypedFormGroup).setControl('accountRangeTable', this.rangeForm);
				}
			}
		});
	}

	createElementModel(element: string, mask: string, startIdx: number): AccountElementModel {
		return {
			element,
			maxLength: mask.length,
			maxValue: mask.replace(/0/g, '9'),
			fromValue: (() => {
				if (!this.props.initValues) {
					return null;
				}
				return mask;
			})(),
			toValue: (() => {
				if (!this.props.initValues) {
					return null;
				}
				return mask.replace(/0/g, '9');
			})(),
			accountTypeId: 0,
			startIndex: startIdx,
		};
	}

	createRowFormGroup(element: AccountElementModel): UntypedFormGroup {
		const validators = [this.fromLessThanToValidator, this.fromAndToRequired];

		return this.formBuilder.group(
			{
				accountTypeId: [element.accountTypeId],
				element: [element.element],
				fromValue: [element.fromValue],
				toValue: [element.toValue],
				startIndex: [element.startIndex],
				maxLength: [element.maxLength],
				maxValue: [element.maxValue],
			},
			{ validators }
		);
	}

	fromAndToRequired(control: UntypedFormControl) {
		const fromValue = parseInt(control.value.fromValue, 10);
		const toValue = parseInt(control.value.toValue, 10);
		if (!!toValue && Number.isNaN(fromValue)) {
			return { fromRequired: true };
		}
		if (!!fromValue && Number.isNaN(toValue)) {
			return { toRequired: true };
		}
		return null;
	}

	fromLessThanToValidator(control: UntypedFormControl): { [key: string]: boolean } | null {
		const fromValue = parseInt(control.value.fromValue, 10);
		const toValue = parseInt(control.value.toValue, 10);
		if (fromValue !== null && toValue !== null && toValue < fromValue) {
			return { fromLessThanTo: true };
		}
		return null;
	}

	clearAll(): void {
		((this.form.get('accountRangeTable') as UntypedFormGroup).controls.rows as UntypedFormArray).controls.forEach((el: UntypedFormGroup) => {
			el.controls.fromValue.setValue('');
			el.controls.toValue.setValue('');
		});
		this.value = [];
	}

	onChange(acctF: AccountElementModel, fromValue: boolean): void {
		const i = this.RangeTableModel.findIndex((el) => el === acctF);
		const acctFcontrol = ((this.form.get('accountRangeTable') as UntypedFormGroup).controls.rows as UntypedFormArray).controls[
			i
		] as UntypedFormGroup;
		if (fromValue && AccountRangeTableComponent.elementValidator.test(acctFcontrol.controls.fromValue.value)) {
			const defaultValue = acctF.maxValue;
			acctFcontrol.controls.toValue.setValue(isNil(acctF.toValue) || acctF.toValue === '' ? defaultValue : acctF.toValue);
		}
		this.RangeTableModel[i].toValue = acctFcontrol.controls.toValue.value;
		this.RangeTableModel[i].fromValue = acctFcontrol.controls.fromValue.value;
		this.value = this.RangeTableModel.filter((el) => !!el.fromValue && !!el.toValue);
		if ((acctF.fromValue === null || acctF.fromValue === '') && acctF.toValue !== null && acctF.toValue !== '') {
			acctFcontrol.setErrors({
				toValueExists: true,
			});
		} else if (parseInt(acctF.fromValue, 10) > parseInt(acctF.toValue, 10)) {
			acctFcontrol.setErrors({
				fromLessThanTo: true,
			});
		} else if (parseInt(acctF.maxValue, 10) < parseInt(acctF.fromValue, 10) || parseInt(acctF.maxValue, 10) < parseInt(acctF.toValue, 10)) {
			acctFcontrol.setErrors({
				maxMinValue: true,
			});
		}
	}

	onPaste(event: ClipboardEvent, acctF: AccountElementModel, fromValue: boolean): void {
		const i = this.RangeTableModel.findIndex((el) => el === acctF);
		const acctFcontrol = ((this.form.get('accountRangeTable') as UntypedFormGroup).controls.rows as UntypedFormArray).controls[
			i
		] as UntypedFormGroup;
		const pastedText = parseFloat(event.clipboardData.getData('text'));
		event.preventDefault();
		if (!isNaN(pastedText)) {
			let clippedValue = pastedText.toString().substring(0, acctF.maxLength);
			if (pastedText > +acctF.maxValue) {
				clippedValue = clippedValue.substring(0, acctF.maxLength);
			}
			if (fromValue) {
				acctFcontrol.controls.fromValue.setValue(null);
				acctFcontrol.controls.fromValue.setValue(clippedValue);
			} else {
				acctFcontrol.controls.toValue.setValue(null);
				acctFcontrol.controls.toValue.setValue(clippedValue);
			}
			this.onChange(acctF, fromValue);
		}
	}
}
